<template>
  <div :id="`${source}_place_call_component`">
    <CustomDropdown
      ref="callDropdown"
      v-if="!isCallActive"
      max-height="300px"
      gap="10px"
      position="start"
      :overflow-y="overflowOptions.VISIBLE"
      :relative-to-viewport="true"
      :disabled="!hasDialerAccess"
    >
      <template #trigger>
        <a-button v-if="!iconAction" class="action_button">
          <ThemeImage
            class="size-4"
            :icon="imageNames.CALL_UNSELECTED"
            alt="action_icon"
          />
          Call
        </a-button>

        <div v-else class="quick_action_item mt-1">
          <a-tooltip placement="top" title="Place a phone call">
            <ThemeImage class="size-5" :icon="imageNames.CALL_GREY" alt="call_icon" />
          </a-tooltip>
        </div>
      </template>

      <template #default>
        <div
          class="flex flex-col gap-4 p-4 secondary_color text-[13px] font-medium"
        >
          <div class="flex flex-col gap-2">
            <span>Call</span>
            <a-select v-model:value="phoneNumber" class="w-48">
              <template #suffixIcon>
                <ThemeImage
                  :icon="imageNames.BOTTOM_ARROW"
                  alt="bottom_white_arrow"
                  class="size-4 opacity-60"
                />
              </template>

              <a-select-option
                v-for="(number, index) in multiplePhoneNumbers"
                :key="`${number.id}_number_${index}`"
                :value="number.phone_number"
                :label="number.type"
              >
                {{ number.phone_number }} ({{ number.type }})
              </a-select-option>
            </a-select>
          </div>

          <div class="flex flex-col gap-2">
            <span>Call via</span>
            <a-select v-model:value="callParams.From" class="w-48">
              <template #suffixIcon>
                <ThemeImage
                  :icon="imageNames.BOTTOM_ARROW"
                  alt="bottom_white_arrow"
                  class="size-4 opacity-60"
                />
              </template>

              <a-select-option
                v-for="(number, index) in callViaList"
                :key="`${number.id}_number_${index}`"
                :value="number.phone_number"
                :label="number.phone_number"
              >
                {{ number.phone_number }}
              </a-select-option>
            </a-select>
          </div>

          <a-button
            class="primary_medium w-full"
            @click="handleCall"
            :disabled="isCallDisabled"
          >
            <ThemeImage
              class="size-4"
              :icon="imageNames.CALL_BLACK"
              alt="call_icon_black"
            />
            Place Call
          </a-button>
        </div>
      </template>
    </CustomDropdown>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, nextTick } from "vue";

import CustomDropdown from "@/src/components/CustomDropdown.vue";
import {
  initializeAndMakeCall,
  toGetPhoneNumbers,
} from "@/src/utils/callsUtils.js";
import { overflowOptions } from "@/src/constants/customComponentConstants.js";
import { isCallActive } from "@/src/utils/callsUtils.js";
import sidebarImagePath from "@/src/paths/sidebarImagePath.js";
import store from "@/src/store/index.js";

import { imageNames } from "@/src/constants/imageConstants.js";

const props = defineProps({
  source: { type: String, required: true },
  entity: { type: Object, required: true },
  taskId: { type: [String, Number], default: 0 },
  iconAction: { type: Boolean, required: false, default: false },
  multipleEntries: { type: Boolean, required: false, default: false },
  triggerOpenDropdown: { type: Boolean, default: false },
});

const emit = defineEmits(["dropdownOpened"]);

const callViaList = ref(null);
const callDropdown = ref(null);
const entityDetails = ref(props.entity);
const phoneNumber = ref(props.entity?.phone_number);
const callParams = ref({
  To: props.entity?.phone_number,
  From: null,
});
let multiplePhoneNumbers = ref([]);
const hasDialerAccess = computed(() => {
  const storeAccess = store.getters["rbac/hasDialerAccess"];
  if (storeAccess) return true;

  // Fallback to localStorage while store is loading (race condition fix)
  try {
    const permissions = JSON.parse(
      localStorage.getItem("userPermissions") || "[]"
    );
    return permissions.includes("dialer");
  } catch {
    return false;
  }
});

const isCallDisabled = computed(() => {
  return (
    !phoneNumber.value ||
    !callParams.value.From ||
    phoneNumber.value === callParams.value.From
  );
});

const handleCall = async () => {
  await initializeAndMakeCall({
    phoneNumber: phoneNumber.value,
    callParams: callParams.value,
    entityDetails: entityDetails.value,
    isCampaign: false,
    entityType: props.source,
    taskId: props.taskId,
  });
};

const fetchContactPhoneNumbers = async () => {
  multiplePhoneNumbers.value = [];

  const entity = props.entity;
  if (!entity) return;

  if (entity?.phone_numbers?.length) {
    multiplePhoneNumbers.value.push(
      ...entity.phone_numbers.map((num) => ({
        phone_number: num.phone_number,
        type: num.type || num.phone_type || "Other",
      }))
    );
  }

  if (entity?.company_phone_numbers?.length) {
    multiplePhoneNumbers.value.push(
      ...entity.company_phone_numbers.map((num) => ({
        phone_number: num.phone_number,
        type: num.type || num.phone_type || "Company",
      }))
    );
  }

  const customProperties = entity?.custom_properties || {};

  Object.values(customProperties).forEach((value) => {
    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (
          item &&
          typeof item === "object" &&
          item.phone_type &&
          item.normalized_phone_number
        ) {
          multiplePhoneNumbers.value.push({
            phone_number: item.normalized_phone_number,
            type: item.phone_type,
          });
        }
      });
    }
  });

  multiplePhoneNumbers.value = [
    ...new Map(
      multiplePhoneNumbers.value.map((item) => [
        item.phone_number,
        item,
      ])
    ).values(),
  ];

  if (multiplePhoneNumbers.value.length) {
  phoneNumber.value = multiplePhoneNumbers.value[0].phone_number;
  callParams.value.To = phoneNumber.value;
  }
};


watch(
  () => props.entity?.phone_number,
  (newNumber) => {
    phoneNumber.value = newNumber;
    callParams.value.To = newNumber;
  },
  { immediate: true },
);

watch(
  () => props.entity,
  (entityVal) => {
    callParams.value.To = entityVal?.phone_number;
  },
  { immediate: true },
);

const fetchPhoneNumbers = async () => {
  await fetchContactPhoneNumbers();
  const filters = { user: true };
  callViaList.value = await toGetPhoneNumbers(filters);
  if (callViaList.value) {
    callParams.value.From = callViaList.value[0]?.phone_number;
  }
};

watch(
  () => callDropdown.value?.isOpen,
  async (isOpen) => {
    if (isOpen && props.multipleEntries) {
      await fetchPhoneNumbers();
    }
  },
);

onMounted(async () => {
  if (!props.multipleEntries) {
    await fetchPhoneNumbers();
  }
});

watch(
  () => props.triggerOpenDropdown,
  async (newVal, oldVal) => {
    if (newVal && newVal !== oldVal) {
      await nextTick();
      if (callDropdown.value && !callDropdown.value.isOpen) {
        callDropdown.value.toggleDropdown();
        emit("dropdownOpened", true);
      }
    }
  },
);
</script>
